import React from "react";
import { useNavigate } from "react-router-dom";
import { EditFilled, DeleteFilled } from "@ant-design/icons";
import { t } from "i18next";
import swal from "sweetalert";
import {
  Row,
  Col,
  Table,
  Button,
  Input,
  SearchOutlined,
  PlusOutlined,
  Form,
} from "common/elements";
import { Util } from "common/util";

export default function withDataTable({
  title = "",
  columns = [],
  showActionAdd = true,
  showActionDelete = true,
  getData = () => {},
  permission = null,
  addNewLink = "",
  updateLink = "",
  pathName = "/",
  FormInsertOrUpdate = () => {},
  handleAddNew = (modalRef) => {},
  handleShowUpdate = (id, modalRef) => {},
  confirmSoftDelete = (id) => {},
  handleSearch = (e) => {},
  defaultPageSize = 20,
}) {
  return () => {
    let modalRef = React.createRef();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    const util = new Util();
    const [datas, setDatas] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const params = new URLSearchParams(document.location.search);
    let timer = null;

    const onSearch = (e) => {
      clearTimeout(timer);
      handleSearch(e);
      timer = setTimeout(() => {
        fetchList();
      }, 1000);
    };

    const onAddNew = () => {
      let hasNoPermission = false;
      if (
        permission &&
        Util.getInstance().hasNoPermission("create", permission)
      ) {
        hasNoPermission = true;
      }

      if (hasNoPermission) {
        Util.getInstance().sweetAlertMessage(
          t("text_no_permission"),
          "warning"
        );
      } else {
        if (addNewLink) {
          navigate(addNewLink);
        } else {
          handleAddNew(modalRef);
        }
      }
    };

    const onShowUpdateForm = (id) => {
      let hasNoPermission = false;
      if (
        permission &&
        Util.getInstance().hasNoPermission("edit", permission)
      ) {
        hasNoPermission = true;
      }
      if (hasNoPermission) {
        Util.getInstance().sweetAlertMessage(
          t("text_no_permission"),
          "warning"
        );
      } else {
        if (updateLink) {
          navigate(updateLink + `/${id}`);
        } else {
          handleShowUpdate(id, modalRef);
        }
      }
    };

    const fetchList = () => {
      setLoading(true);
      getData()
        .then((response) => {
          setDatas(response);
          setLoading(false);
        })
        .catch(error => {
          if (error.response.status === 403) {
            localStorage.removeItem("ACCESS_TOKEN");
            navigate("/signin");
          }
        })
        .finally(() => setLoading(false));
    };

    const onDelete = (id) => {
      let hasNoPermission = false;
      if (
        permission &&
        Util.getInstance().hasNoPermission("delete", permission)
      ) {
        hasNoPermission = true;
      }

      if (hasNoPermission) {
        Util.getInstance().sweetAlertMessage(
          t("text_no_permission"),
          "warning"
        );
      } else {
        util.sweetAlertConfirm(t("text_confrim")).then((willDelete) => {
          if (willDelete) {
            confirmSoftDelete(id)
              .then(() => fetchList())
              .catch((error) => {
                swal({
                  title: "Opps!",
                  text: error?.response?.data?.error?.message,
                  icon: "error",
                  dangerMode: true,
               });
              });
          }
        });
      }
    };

    const handleTableChange = (newPagination, filters, sorter) => {
      fetchList({
        sortField: sorter.field,
        sortOrder: sorter.order,
        pagination: newPagination,
        ...filters,
      });
    };

    const onChangePagination = (page, pageSize) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", page);
      params.set("size", pageSize);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
    };

    const onShowSizeChange = (current, size) => {
      const params = new URLSearchParams(document.location.search);
      params.set("current", current);
      params.set("size", size);
      Util.getInstance().pushParamsToURL(pathName, params.toString());
      fetchList();
    };

    React.useEffect(() => {
      fetchList();
       // eslint-disable-next-line
    }, []);

    const pagination =
      datas.pagination?.total > datas.pagination?.limit
        ? {
            current: params.has("current")
              ? parseInt(params.get("current"))
              : 1,
            defaultPageSize,
            pageSizeOptions: ["20", "30", "50", "100"],
            total: datas.pagination?.total,
            showSizeChanger: true,
            locale: { items_per_page: "" },
            onChange: onChangePagination,
            onShowSizeChange,
            showTotal: (total, range) =>
              `${t("text_display")} ${range[0]}-${range[1]} ${t(
                "text_out_of"
              )} ${total}`,
          }
        : true;
    return (
      <>
        <Row style={{ marginBottom: 15 }}>
          <Col span={12}>
            <Title title={title} />
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Input
              placeholder="Search"
              prefix={<SearchOutlined />}
              allowClear={true}
              style={styles.generalSearch}
              onChange={onSearch}
            />
            {
              showActionAdd && 
              <Button type="primary" icon={<PlusOutlined />} onClick={onAddNew}>
                {t("text_add_new")}
              </Button>
            }
          </Col>
        </Row>

        <div className="data-table">
          <Table
            dataSource={datas.data}
            pagination={pagination}
            onChange={handleTableChange}
            rowKey="id"
            scroll={datas.pagination ? {y: window.innerHeight - 310} : {} }
            columns={[
              ...columns,
              {
                title: t("text_action"),
                dataIndex: "id",
                key: "id",
                render: (id) => {
                  return (
                    <div id="btn-action">
                      <div id="btnUpdateSetting">
                        <Button
                          style={{
                            height: 40,
                            width: 40,
                            border: "none",
                            color: "#000000",
                            // opacity: 0.45,
                          }}
                          type="primary"
                          ghost
                          title={`${t("text_update")}`}
                          onClick={() => onShowUpdateForm(id)}
                          icon={<EditFilled />}
                        />
                      </div>
                      {
                        showActionDelete && 
                        <div id="btnDeleteSetting">
                          <Button
                            danger
                            title={t("text_delete")}
                            icon={<DeleteFilled />}
                            style={{
                              height: 40,
                              width: 40,
                              border: "none",
                              color: "#000000",
                              opacity: 0.45,
                            }}
                            onClick={() => onDelete(id)}
                          />
                        </div>
                      }
                    </div>
                  );
                },
              },
            ]}
            loading={loading}
          />
        </div>

        <FormInsertOrUpdate
          ref={(f) => (modalRef = f)}
          onSuccess={fetchList}
          form={form}
          navigate={navigate}
        />
      </>
    );
  };
}

function Title({ title }) {
  return <h3 style={{ marginBottom: 0, fontWeight: 600 }}>{title}</h3>;
}

const styles = {
  generalSearch: {
    width: 186,
    marginRight: 10,
  },
  headerFilter: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};
